import request from '@/utils/request'

/**
 * 回测 API
 */
export const backtestApi = {
  /**
   * 创建回测
   * @param {Object} data
   * @param {string} data.start_date - 开始日期 YYYY-MM-DD
   * @param {string} [data.end_date] - 结束日期 YYYY-MM-DD
   * @param {number} [data.initial_capital] - 初始资金
   * @param {string} [data.ai_model] - AI 模型
   */
  create (data) {
    return request({
      url: '/backtest',
      method: 'post',
      data
    })
  },

  /**
   * 回测列表
   */
  getList (params = {}) {
    return request({
      url: '/backtest',
      method: 'get',
      params: {
        page_num: 1,
        page_size: 20,
        ...params
      }
    })
  },

  /**
   * 获取回测详情
   */
  getById (runId) {
    return request({
      url: `/backtest/${runId}`,
      method: 'get'
    })
  },

  /**
   * 手动执行下一天
   */
  step (runId) {
    return request({
      url: `/backtest/${runId}/step`,
      method: 'post'
    })
  },

  /**
   * 异步执行下一天
   */
  stepAsync (runId) {
    return request({
      url: `/backtest/${runId}/step-async`,
      method: 'post'
    })
  },

  /**
   * 自动运行到结束日期
   */
  auto (runId, endDate) {
    return request({
      url: `/backtest/${runId}/auto`,
      method: 'post',
      params: endDate ? { end_date: endDate } : {}
    })
  },

  /**
   * 停止当前回测正在执行的后台任务
   */
  cancel (runId) {
    return request({
      url: `/backtest/${runId}/cancel`,
      method: 'post'
    })
  },

  /**
   * 查询自动运行任务状态
   */
  getTaskStatus (taskId) {
    return request({
      url: `/backtest/task/${taskId}`,
      method: 'get'
    })
  },

  /**
   * 查询指定回测是否有正在执行的后台任务
   */
  getRunningTask (runId) {
    return request({
      url: `/backtest/${runId}/running-task`,
      method: 'get'
    })
  },

  /**
   * 获取交易记录
   */
  getTrades (runId, params = {}) {
    return request({
      url: `/backtest/${runId}/trades`,
      method: 'get',
      params: {
        page_num: 1,
        page_size: 100,
        ...params
      }
    })
  },

  /**
   * 获取每日快照
   */
  getSnapshots (runId, params = {}) {
    return request({
      url: `/backtest/${runId}/snapshots`,
      method: 'get',
      params: {
        page_num: 1,
        page_size: 100,
        ...params
      }
    })
  },

  /**
   * 获取 AI 决策日志
   */
  getDecisions (runId, params = {}) {
    return request({
      url: `/backtest/${runId}/decisions`,
      method: 'get',
      params: {
        page_num: 1,
        page_size: 100,
        ...params
      }
    })
  },

  /**
   * 删除回测
   */
  delete (runId) {
    return request({
      url: `/backtest/${runId}`,
      method: 'delete'
    })
  },

  /**
   * 撤回一天
   */
  revert (runId) {
    return request({
      url: `/backtest/${runId}/revert`,
      method: 'post'
    })
  },

  /**
   * 批量删除回测
   * @param {number[]} [runIds] - 指定要删除的回测 ID 列表，不传则清空全部
   */
  batchDelete (runIds = null) {
    return request({
      url: '/backtest/batch-delete',
      method: 'post',
      data: runIds ? { run_ids: runIds } : {}
    })
  },

  /**
   * 获取提示词分段列表
   */
  getPromptSegments () {
    return request({
      url: '/backtest/prompt',
      method: 'get'
    })
  },

  /**
   * 保存提示词分段（仅可编辑分段生效）
   * @param {Array<{segment_key: string, content: string}>} segments
   * @param {string} [note] - 版本备注
   */
  savePromptSegments (segments, note = '') {
    return request({
      url: '/backtest/prompt',
      method: 'put',
      data: { segments, note: note || undefined }
    })
  },

  /**
   * AI 润色提示词分段
   * @param {Array<{segment_key: string, content: string}>} segments
   * @param {string} [focus] - 润色重点
   */
  polishPromptSegments (segments, focus = '') {
    return request({
      url: '/backtest/prompt/polish',
      method: 'post',
      timeout: 200000,
      data: { segments, focus: focus || undefined }
    })
  },

  /**
   * 提示词历史版本列表
   */
  getPromptVersions () {
    return request({
      url: '/backtest/prompt/versions',
      method: 'get'
    })
  },

  /**
   * 获取指定历史版本的分段快照
   * @param {number} version
   */
  getPromptVersion (version) {
    return request({
      url: `/backtest/prompt/versions/${version}`,
      method: 'get'
    })
  },

  /**
   * 回滚到指定历史版本
   * @param {number} version
   * @param {string} [note] - 回滚后的版本备注
   */
  restorePromptVersion (version, note = '') {
    return request({
      url: '/backtest/prompt/restore',
      method: 'post',
      data: { version, note: note || undefined }
    })
  }
}
